<template>
  <div class="broadcast-preview">
    <!-- BANNER -->
    <div v-if="isBanner" class="bp-banner">
      <span class="bp-banner__ribbon" :style="{ background: swatch }"></span>
      <button type="button" class="bp-banner__close" v-if="form.dismissible">×</button>

      <div class="bp-banner__content">
        <p class="bp-banner__title">{{ title || 'Titulo do aviso aparece aqui' }}</p>
        <p class="bp-banner__subtitle">
          {{ subtitle || 'Comece a escrever' }}
        </p>
      </div>

      <div class="bp-banner__image" v-if="form.imageUrl">
        <img :src="form.imageUrl" alt="">
      </div>

      <button type="button" class="bp-banner__cta" v-if="hasTarget">
        {{ form.ctaLabel || 'Ver detalhes' }}
      </button>
    </div>

    <!-- MODAL -->
    <div v-else class="bp-modal">
      <div class="bp-modal__header">
        <span>Avisos e novidades</span>
        <button type="button" class="bp-modal__close">×</button>
      </div>

      <!-- Altura FIXA: o Figma pede que texto longo role aqui dentro em vez de
           esticar a modal. Se o preview crescesse, o autor nao veria o corte. -->
      <div class="bp-modal__body">
        <p class="bp-modal__title">{{ title || 'Titulo do aviso aparece aqui' }}</p>
        <p class="bp-modal__subtitle" v-if="subtitle">{{ subtitle }}</p>

        <div class="bp-modal__image" v-if="form.imageUrl">
          <img :src="form.imageUrl" alt="">
        </div>

        <div class="bp-modal__text" v-if="renderedBody" v-html="renderedBody"></div>
        <p class="bp-modal__text bp-modal__text--empty" v-else>
          Comece a escrever sua mensagem para ver como ela ficara para os usuarios
        </p>
      </div>

      <div class="bp-modal__footer">
        <button type="button" class="bp-modal__link">Ver depois</button>
        <button type="button" class="bp-modal__primary">
          {{ primaryLabel }}
        </button>
      </div>
    </div>

    <p class="broadcast-preview__note">
      Previa aproximada. Cores e tipografia finais vem do design system do
      Backoffice V2.
    </p>
  </div>
</template>

<script>
/**
 * Previa em tempo real da campanha, exigida pelo Figma (node 21987:25057):
 * "conforme o usuario edita titulo, texto ou imagem, a modal de preview deve
 * ser atualizada imediatamente".
 *
 * O corpo passa pelo MESMO renderizador que o preview do Master usa em todo
 * lugar (`renderBroadcastMarkdown`), que por sua vez imita o react-markdown sem
 * rehype-raw do V2 — HTML cru aparece como texto nos dois.
 */
import {
  BROADCAST_COLOR_SWATCHES,
  BROADCAST_TYPE,
  isBroadcastBannerType,
  isValidBroadcastUrl,
  modalButtonLabel,
} from './broadcastEnums';
import { renderBroadcastMarkdown } from './broadcastMarkdown';
import { fillPreview } from './broadcastPlaceholders';

export default {
  name: 'broadcastPreview',
  props: {
    form: { type: Object, required: true },
  },
  computed: {
    title() { return fillPreview(this.form.title); },
    subtitle() { return fillPreview(this.form.subtitle); },
    isBanner() {
      return isBroadcastBannerType(this.form.type);
    },
    hasTarget() {
      return isValidBroadcastUrl(this.form.ctaUrl);
    },
    swatch() {
      const found = BROADCAST_COLOR_SWATCHES.find(
        (item) => item.value === this.form.color
      );
      return found ? found.hex : BROADCAST_COLOR_SWATCHES[0].hex;
    },
    renderedBody() {
      return renderBroadcastMarkdown(fillPreview(this.form.body));
    },
    /**
     * Nota de versao nao redireciona: fecha e pronto. Os outros tipos levam ao
     * destino quando ele e valido. Mesma regra do host do V2.
     */
    primaryLabel() {
      const redirects =
        this.form.type !== BROADCAST_TYPE.RELEASE_NOTE && this.hasTarget;
      return redirects ? this.form.ctaLabel || 'Ver detalhes' : modalButtonLabel(this.form.ctaLabel);
    },
  },
};
</script>

<style scoped>
.broadcast-preview__note {
  margin: 10px 0 0;
  font-size: 11px;
  color: #9ca0a7;
  text-align: center;
}

/* ---------- banner ---------- */
.bp-banner {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 26px 44px 20px 32px;
  background: #fff;
  border: 1px solid #d5d7da;
  border-radius: 12px;
  overflow: hidden;
}

.bp-banner__ribbon {
  position: absolute;
  top: 0;
  left: 18px;
  width: 22px;
  height: 22px;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 72%, 0 100%);
}

.bp-banner__close,
.bp-modal__close {
  position: absolute;
  top: 8px;
  right: 10px;
  border: none;
  background: none;
  font-size: 18px;
  color: #9ca0a7;
  cursor: pointer;
}

.bp-banner__content { flex: 1 1 auto; min-width: 0; }
.bp-banner__title { margin: 0; font-size: 18px; font-weight: 600; color: #3a424e; }
.bp-banner__subtitle { margin: 2px 0 0; font-size: 13px; color: #4e5560; }

/* Mesma caixa dos tres renderizadores: proporcao 407:151 do Figma
   (node 31599:61851), largura relativa ao card, imagem encaixada sem cortar. */
.bp-banner__image {
  flex: 0 0 auto;
  width: 30%;
  max-width: 407px;
  min-width: 140px;
  aspect-ratio: 407 / 151;
}
.bp-banner__image img { display: block; width: 100%; height: 100%; object-fit: contain; }

.bp-banner__cta,
.bp-modal__primary {
  flex: 0 0 auto;
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  background: #1b59f8;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

/* ---------- modal ---------- */
.bp-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #d5d7da;
  border-radius: 12px;
  overflow: hidden;
}

.bp-modal__header {
  padding: 14px 16px;
  border-bottom: 1px solid #eceff3;
  font-size: 13px;
  font-weight: 600;
  color: #3a424e;
}

/* Altura fixa + rolagem interna: e o comportamento que o Figma descreve. */
.bp-modal__body {
  height: 260px;
  overflow-y: auto;
  padding: 16px;
}

.bp-modal__title { margin: 0 0 10px; font-size: 16px; font-weight: 600; color: #3a424e; }
.bp-modal__subtitle { margin: -4px 0 12px; font-size: 14px; font-weight: 500; color: #8a8d92; }
.bp-modal__image { margin: 0 0 12px; }
.bp-modal__image img { display: block; width: 100%; height: auto; border-radius: 6px; }
/* Os clientes rolam o corpo dentro da modal (altura maxima de 90vh); a
   previa faz o mesmo, para uma lista longa nao parecer que estoura a tela. */
.bp-modal__text {
  max-height: 320px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.5;
  color: #4e5560;
  word-break: break-word;
}
.bp-modal__text--empty { color: #9ca0a7; }

.bp-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #eceff3;
}

.bp-modal__link {
  border: none;
  background: none;
  color: #1b59f8;
  font-size: 13px;
  cursor: pointer;
}

/*
  A previa mora dentro de `.default-box.filters`, e custom.css pinta TODO
  `button:hover` daquele container de azul escuro (`.filters button:hover`).
  Aqui os botoes sao ilustracao, nao controle: nao levam a lugar nenhum, entao
  nao podem reagir ao mouse como se levassem. As regras abaixo repetem o estado
  de repouso e vencem por especificidade (classe + :hover + atributo do scoped).
*/
.bp-banner__close,
.bp-modal__close,
.bp-modal__link,
.bp-modal__primary {
  cursor: default;
}

.bp-banner__close:hover,
.bp-modal__close:hover {
  background: none;
  color: #9ca0a7;
}

.bp-modal__link:hover {
  background: none;
  color: #1b59f8;
}

.bp-modal__primary:hover {
  background: #1b59f8;
  color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/broadcast/broadcastPreview.vue